import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { type Arena } from '@/types/arena.type'
import { Separator } from '@/components/ui/separator'
import axiosInstance, { endpoints } from '@/lib/axios'
import { Card, CardContent } from '@/components/ui/card'
import { RoosterMatchStatus } from '@/types/rooster-match.type'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface RoosterMatchFilters {
  page: number
  limit: number
  matchNumber: string
  status: string
  arenaId: string
  orderBy: string
}

interface RoosterMatchFilterProps {
  filters: RoosterMatchFilters
  onFiltersChange: (filters: Partial<RoosterMatchFilters>) => void
  onAddClick: () => void
}

const statusOptions = [
  { value: RoosterMatchStatus.OPEN, label: 'เปิดรับคู่แข่ง' },
  { value: RoosterMatchStatus.PENDING_APPROVAL, label: 'รอการอนุมัติ' },
  { value: RoosterMatchStatus.APPROVED, label: 'อนุมัติแล้ว' },
  { value: RoosterMatchStatus.REJECTED, label: 'ปฏิเสธ' },
  { value: RoosterMatchStatus.CANCELLED, label: 'ยกเลิก' },
  { value: RoosterMatchStatus.COMPLETED, label: 'เสร็จสิ้น' },
  { value: RoosterMatchStatus.IN_PROGRESS, label: 'กำลังแข่งขัน' },
]

const orderByOptions = [
  { value: 'createdAt:desc', label: 'วันที่สร้าง (ใหม่สุด)' },
  { value: 'createdAt:asc', label: 'วันที่สร้าง (เก่าสุด)' },
  { value: 'matchDate:desc', label: 'วันที่แข่งขัน (ใหม่สุด)' },
  { value: 'matchDate:asc', label: 'วันที่แข่งขัน (เก่าสุด)' },
  { value: 'minBetAmount:desc', label: 'เดิมพันสูงสุด' },
  { value: 'minBetAmount:asc', label: 'เดิมพันต่ำสุด' },
]

export default function RoosterMatchFilter({
  filters,
  onFiltersChange,
}: RoosterMatchFilterProps) {
  const [searchValue, setSearchValue] = useState(filters.matchNumber)
  const [arenas, setArenas] = useState<Arena[]>([])
  const [loadingArenas, setLoadingArenas] = useState(false)

  // Fetch arenas for dropdown
  useEffect(() => {
    const fetchArenas = async () => {
      try {
        setLoadingArenas(true)
        const { data } = await axiosInstance.get<{
          items: Arena[]
          total: number
        }>(endpoints.arenas.list, {
          params: { limit: 100 }, // Get all arenas for dropdown
        })
        setArenas(data.items)
      } catch (error) {
        console.error('Error fetching arenas:', error)
        setArenas([])
      } finally {
        setLoadingArenas(false)
      }
    }

    fetchArenas()
  }, [])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    onFiltersChange({ matchNumber: value, page: 1 })
  }

  const handleFilterChange = (
    key: keyof RoosterMatchFilters,
    value: string,
  ) => {
    onFiltersChange({ [key]: value, page: 1 })
  }

  const getStatusValue = () => {
    return filters.status || 'all'
  }

  const handleStatusChange = (value: string) => {
    const status = value === 'all' ? '' : value
    onFiltersChange({
      ...filters,
      status,
      page: 1,
    })
  }

  const handleArenaChange = (value: string) => {
    const arenaId = value === 'all' ? '' : value
    onFiltersChange({
      ...filters,
      arenaId,
      page: 1,
    })
  }

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          {/* Header with Add Button */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              จัดการจับคู่แข่งขัน
            </h2>
          </div>

          <Separator className="border-gray-200" />

          {/* Search */}
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-500 mb-1">ค้นหา</p>
            <Input
              placeholder="ค้นหาตามรหัสการแข่งขัน"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="min-w-[300px]"
            />
          </div>

          <div className="flex gap-6 flex-wrap">
            {/* Status Filter */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">สถานะ</p>
              <Select
                value={getStatusValue()}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="min-w-[140px]">
                  <SelectValue placeholder="เลือกสถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกสถานะ</SelectItem>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Arena Filter */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สนามแข่งขัน
              </p>
              <Select
                value={filters.arenaId || 'all'}
                onValueChange={handleArenaChange}
                disabled={loadingArenas}
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue
                    placeholder={
                      loadingArenas ? 'กำลังโหลด...' : 'เลือกสนามแข่งขัน'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกสนาม</SelectItem>
                  {arenas.map((arena) => (
                    <SelectItem key={arena.id} value={arena.id}>
                      {arena.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Order By Filter */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">เรียงตาม</p>
              <Select
                value={filters.orderBy}
                onValueChange={(value) => handleFilterChange('orderBy', value)}
              >
                <SelectTrigger className="min-w-[140px]">
                  <SelectValue placeholder="เลือกการเรียงลำดับ" />
                </SelectTrigger>
                <SelectContent>
                  {orderByOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
